/**
 * bl-product.js — product page behaviour.
 *
 * Three small registry components plus one custom element. Everything is
 * progressive enhancement: with this file blocked the page still sells
 * (native product form, native <select name="id">, native number input,
 * native <details> accordions, native scroll-snap gallery).
 *
 *   product-gallery  thumbnail sync, arrows, zoom dialog
 *   quantity         accessible +/- buttons bound to the native input
 *   product          variant-driven presentation state, sticky bar
 *
 * Add to cart is Shopify's native product form: this file never intercepts,
 * preventDefault()s or re-posts it. Shopify owns commerce.
 *   <variant-picker> Shopify variant data -> id input, price, media, state
 *
 * Shopify stays the source of truth: variant data comes from product.variants
 * as rendered by Liquid, and nothing here duplicates cart or checkout.
 */

const BL = window.BrightLeaf;

/* ---------------------------------------------------------------- gallery */

BL.components.register('product-gallery', (root, { utils }) => {
  const viewer = root.querySelector('.bl-gallery__viewer');
  const slides = [...root.querySelectorAll('.bl-gallery__slide')];
  const thumbs = [...root.querySelectorAll('[data-bl-gallery-thumb]')];
  const arrows = root.querySelector('[data-bl-gallery-arrows]');
  const dialog = root.querySelector('[data-bl-gallery-dialog]');
  const dialogBody = root.querySelector('[data-bl-gallery-dialog-body]');
  if (!viewer || !slides.length) return undefined;

  const behavior = () => (utils.prefersReducedMotion() ? 'auto' : 'smooth');

  const setActive = (index) => {
    thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('bl-gallery__thumb--active', i === index);
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  };

  const goTo = (index, focus = false) => {
    const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
    if (!slide) return;
    viewer.scrollTo({ left: slide.offsetLeft - viewer.offsetLeft, behavior: behavior() });
    setActive(slides.indexOf(slide));
    if (focus) slide.querySelector('img, video, iframe')?.focus?.({ preventScroll: true });
  };

  const currentIndex = () => {
    const mid = viewer.scrollLeft + viewer.clientWidth / 2;
    return slides.findIndex((s) => s.offsetLeft - viewer.offsetLeft + s.clientWidth > mid);
  };

  const onScroll = utils.throttle(() => {
    const index = currentIndex();
    if (index >= 0) setActive(index);
  }, 120);

  const onThumbClick = (event) => {
    const thumb = event.target.closest('[data-bl-gallery-thumb]');
    if (!thumb) return;
    event.preventDefault();
    goTo(thumbs.indexOf(thumb));
  };

  const onArrow = (event) => {
    const next = event.target.closest('[data-bl-gallery-next]');
    const prev = event.target.closest('[data-bl-gallery-prev]');
    if (!next && !prev) return;
    const index = Math.max(0, currentIndex());
    goTo(index + (next ? 1 : -1));
  };

  const onKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    event.preventDefault();
    goTo(Math.max(0, currentIndex()) + (event.key === 'ArrowRight' ? 1 : -1));
  };

  const openZoom = (event) => {
    const trigger = event.target.closest('[data-bl-gallery-zoom-open]');
    if (!trigger || !dialog || !dialogBody) return;
    const image = trigger.closest('.bl-gallery__slide')?.querySelector('img');
    if (!image) return;
    dialogBody.replaceChildren();
    const clone = image.cloneNode();
    clone.className = 'bl-gallery__dialog-image';
    clone.loading = 'eager';
    clone.sizes = '100vw';
    dialogBody.append(clone);
    dialog.showModal();
    BL.ui.lockScroll();
  };

  const closeZoom = (event) => {
    if (!event.target.closest('[data-bl-gallery-zoom-close]')) return;
    dialog?.close();
  };

  const onDialogClose = () => {
    BL.ui.unlockScroll();
    dialogBody?.replaceChildren();
  };

  /* Only reveal arrows once JS can drive them. */
  if (arrows) {
    arrows.hidden = false;
    arrows.removeAttribute('aria-hidden');
    arrows.querySelectorAll('button').forEach((button) => button.removeAttribute('tabindex'));
  }

  /** Public: select the slide that shows a given media id (variant change). */
  root.selectMedia = (mediaId) => {
    const index = slides.findIndex((slide) => slide.dataset.blMediaId === String(mediaId));
    if (index >= 0) goTo(index);
  };

  viewer.addEventListener('scroll', onScroll, { passive: true });
  viewer.addEventListener('keydown', onKeydown);
  root.addEventListener('click', onThumbClick);
  root.addEventListener('click', onArrow);
  root.addEventListener('click', openZoom);
  dialog?.addEventListener('click', closeZoom);
  dialog?.addEventListener('close', onDialogClose);

  return () => {
    viewer.removeEventListener('scroll', onScroll);
    viewer.removeEventListener('keydown', onKeydown);
    root.removeEventListener('click', onThumbClick);
    root.removeEventListener('click', onArrow);
    root.removeEventListener('click', openZoom);
    dialog?.removeEventListener('click', closeZoom);
    dialog?.removeEventListener('close', onDialogClose);
  };
});

/* --------------------------------------------------------------- quantity */

BL.components.register('quantity', (root) => {
  const input = root.querySelector('.bl-quantity__input');
  if (!input) return undefined;

  const sync = () => {
    const value = Number(input.value) || Number(input.min) || 1;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    root.querySelectorAll('[data-bl-quantity-step]').forEach((button) => {
      const step = Number(button.dataset.blQuantityStep);
      button.disabled = step < 0 ? value <= min : value >= max;
    });
  };

  const onClick = (event) => {
    const button = event.target.closest('[data-bl-quantity-step]');
    if (!button) return;
    const step = Number(input.step) || 1;
    input.stepUp?.call(input, Number(button.dataset.blQuantityStep) * step) ??
      (input.value = Number(input.value) + Number(button.dataset.blQuantityStep) * step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  root.addEventListener('click', onClick);
  input.addEventListener('change', sync);
  input.addEventListener('input', sync);
  sync();

  return () => {
    root.removeEventListener('click', onClick);
    input.removeEventListener('change', sync);
    input.removeEventListener('input', sync);
  };
});

/* --------------------------------------------------------- variant picker */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-bl-variant-data]');
    try {
      this.variants = JSON.parse(data?.textContent || '[]');
    } catch {
      this.variants = [];
    }
    this.section = this.closest('[data-bl-component~="product"]');
    this.addEventListener('change', this.#onChange);
    this.#paintAvailability();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  get selectedOptions() {
    const values = [];
    this.querySelectorAll('[data-bl-option-input]').forEach((input) => {
      const position = Number(input.dataset.blOptionPosition || input.dataset.blOptionInput);
      if (input.tagName === 'SELECT') values[position - 1] = input.value;
      else if (input.checked) values[position - 1] = input.value;
    });
    return values;
  }

  #match(options = this.selectedOptions) {
    return this.variants.find((variant) => variant.options.every((value, i) => value === options[i]));
  }

  /* A value is unavailable when no in-stock variant pairs it with the other
     currently selected options — the same rule Shopify uses in checkout. */
  #paintAvailability() {
    const selected = this.selectedOptions;
    this.querySelectorAll('[data-bl-option-input]').forEach((input) => {
      const position = Number(input.dataset.blOptionInput);
      if (input.tagName === 'SELECT') {
        [...input.options].forEach((option) => {
          const probe = [...selected];
          probe[position - 1] = option.value;
          option.disabled = !this.variants.some((v) => v.options.every((value, i) => value === probe[i]) && v.available);
        });
        return;
      }
      const probe = [...selected];
      probe[position - 1] = input.value;
      const available = this.variants.some((v) => v.options.every((value, i) => value === probe[i]) && v.available);
      input.classList.toggle('bl-variant__input--unavailable', !available);
      input.nextElementSibling?.classList.toggle('bl-variant__label--unavailable', !available);
    });
  }

  #onChange = () => {
    const variant = this.#match();
    this.#paintAvailability();

    this.querySelectorAll('[data-bl-option-selected]').forEach((node) => {
      const position = Number(node.dataset.blOptionSelected);
      node.textContent = this.selectedOptions[position - 1] || '';
    });

    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    this.section?.updateVariant?.(variant || null);
    BL.events.emit('bl:variant:changed', { variant, product: this.section?.dataset.blProduct });
  };
}

if (!window.customElements.get('variant-picker')) {
  window.customElements.define('variant-picker', VariantPicker);
}

/* --------------------------------------------------------- product section */

BL.components.register('product', (root, { utils }) => {
  const submit = root.querySelector('[data-bl-submit-button]');
  const idInput = root.querySelector('[data-bl-variant-id]');
  const priceTarget = root.querySelector('[data-bl-price-target]');
  const stickyBar = root.querySelector('[data-bl-sticky-atc-bar]');
  const stickySubmit = root.querySelector('[data-bl-sticky-submit]');
  const gallery = root.querySelector('[data-bl-gallery]');

  /* The hidden id field ships disabled so the <noscript> variant <select> is
     authoritative without JavaScript. With JS present, this field takes over. */
  if (idInput) idInput.disabled = false;



  /* --- variant state (owned by Shopify data, applied to the DOM here) --- */
  root.updateVariant = (variant) => {
    const sold = !variant || !variant.available;

    if (idInput && variant) idInput.value = variant.id;
    if (submit) {
      submit.disabled = sold;
      submit.textContent = variant
        ? sold
          ? submit.dataset.blLabelSoldOut
          : submit.dataset.blLabelAdd
        : submit.dataset.blLabelUnavailable;
    }
    if (stickySubmit) stickySubmit.disabled = sold;

    if (variant && priceTarget) {
      priceTarget.querySelector('.bl-price__current')?.replaceChildren(document.createTextNode(utils.money(variant.price)));
      const compare = priceTarget.querySelector('.bl-price__compare');
      if (compare) {
        const onSale = variant.compare_at_price > variant.price;
        compare.hidden = !onSale;
        if (onSale) compare.replaceChildren(document.createTextNode(utils.money(variant.compare_at_price)));
      }
    }

    const stickyPrice = root.querySelector('[data-bl-sticky-price]');
    if (variant && stickyPrice) stickyPrice.textContent = utils.money(variant.price);

    const sku = root.querySelector('[data-bl-sku]');
    if (sku) sku.textContent = variant?.sku ? `${sku.dataset.blSkuLabel}: ${variant.sku}` : '';

    const availability = root.querySelector('[data-bl-availability]');
    if (availability) {
      availability.classList.toggle('bl-product__availability--out', sold);
      availability.textContent = sold ? availability.dataset.blSoldOut : availability.dataset.blInStock;
    }

    if (variant?.featured_media?.id) gallery?.selectMedia?.(variant.featured_media.id);
  };

  /* ------------------------------------------------------- sticky add-to-cart */
  let observer;
  if (stickyBar && submit) {
    observer = new IntersectionObserver(
      ([entry]) => {
        stickyBar.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      },
      { rootMargin: '0px 0px -80% 0px' },
    );
    observer.observe(submit);
  }

  return () => {
    observer?.disconnect();
  };
});
